// LOCPICK1 (docs/build-briefs/LOCPICK1-franchisor-location-picker.md, design/mockups/franchisor-location-picker.html): the shared
// location picker. One location, selected locations or all locations; search; state filter when the locations carry a state; a
// list grouped by franchisee with a tri-state group; a live count; removable chips. Text is set with textContent only.
import { hoverHelp } from "./hoverHelp.ts";
import {
  CHIP_CAP,
  canApply,
  chipSplit,
  clearAll,
  countText,
  filterLocations,
  groupState,
  hasSuspended,
  HIDE_SUSPENDED_LABEL,
  SUSPENDED_LABEL,
  pickerGroups,
  selectAll,
  showSearch,
  statesOf,
  toggleGroup,
  toggleLocation,
  withMode,
  type PickerLocation,
  type PickerMode,
  type PickerValue,
} from "./locationPickerModel.ts";

export interface LocationPickerOptions {
  locations: readonly PickerLocation[];
  value: PickerValue;
  /** The modes this report allows: a report that reads one location offers only One location. */
  modes: readonly PickerMode[];
  /** Popover use (reports): Apply and Cancel. Inline use passes neither. */
  apply?(value: PickerValue): void;
  cancel?(): void;
  /** LOCPICK1: mount inside a dialog with no Apply or Cancel; every change is reported through onChange. */
  inline?: boolean;
  onChange?(value: PickerValue): void;
  /** Inline only: the one-line help under the mode buttons, by mode. */
  modeHints?: Partial<Record<PickerMode, string>>;
  /** RPTLAYOUT1-F1-S4: what is being picked. Absent means locations; the Software Owner picks customers. */
  words?: { title: string; singular: string; plural: string };
}
function modeLabels(words: { singular: string; plural: string }): Readonly<Record<PickerMode, string>> {
  return {
    one: `One ${words.singular}`,
    selected: `Selected ${words.plural}`,
    all: `All ${words.plural}`,
  };
}

function node<K extends keyof HTMLElementTagNameMap>(tag: K, className?: string, text?: string): HTMLElementTagNameMap[K] {
  const element = document.createElement(tag);
  if (className !== undefined) element.className = className;
  if (text !== undefined) element.textContent = text;
  return element;
}
function button(text: string, className: string, action: () => void): HTMLButtonElement {
  const element = node("button", className, text);
  element.type = "button";
  element.addEventListener("click", action);
  return element;
}

export function mountLocationPicker(options: LocationPickerOptions): HTMLElement {
  let value = options.modes.includes(options.value.mode) ? options.value : withMode(options.value, options.modes[0] ?? "all");
  let query = "";
  let state = "";
  let chosenOnly = false;
  let hideSuspended = false;
  const inline = options.inline === true;
  const collapsed = new Set<string>();
  const states = statesOf(options.locations);
  const words = options.words ?? { title: "Locations", singular: "location", plural: "locations" };
  const labels = modeLabels(words);

  const root = node("div", "rx-picker");
  root.dataset["testid"] = "rx-picker";
  const head = node("div", "rx-picker-head");
  head.append(
    options.words === undefined
      ? hoverHelp(node("span", "rx-picker-title", words.title), "Choose one location, some locations, or every location you can see.", "", "the Texas locations side by side.")
      : hoverHelp(node("span", "rx-picker-title", words.title), `Choose one ${words.singular}, some ${words.plural}, or every ${words.singular} you can see.`, "", `just the ${words.plural} you want side by side.`),
  );
  const modes = node("div", "rx-seg");
  modes.setAttribute("role", "group");
  modes.setAttribute("aria-label", `${words.title} to read`);
  const body = node("div", "rx-picker-body");
  const search = node("input", "rx-field");
  search.type = "search";
  search.placeholder = options.words === undefined ? "Search location or franchisee" : `Search ${words.singular}`;
  search.setAttribute("inputmode", "search");
  search.setAttribute("aria-label", `Search ${words.plural}`);
  search.dataset["testid"] = "rx-picker-search";
  const stateSelect = node("select", "rx-field");
  stateSelect.setAttribute("aria-label", "State");
  stateSelect.append(new Option("All states", ""));
  for (const item of states) stateSelect.append(new Option(item, item));
  const find = node("div", "rx-picker-find");
  find.append(search);
  if (inline ? states.length > 1 : states.length > 0) find.append(stateSelect);
  find.hidden = inline && !showSearch(options.locations);
  const strip = node("div", "rx-picker-strip");
  const list = node("div", "rx-picker-list");
  list.dataset["testid"] = "rx-picker-list";
  const chips = node("div", "rx-chips");
  chips.dataset["testid"] = "rx-picker-chips";
  const foot = node("div", "rx-picker-foot");
  const applyButton = button("Apply", "rx-btn rx-btn-primary", () => {
    if (canApply(value)) options.apply?.(value);
  });
  applyButton.dataset["testid"] = "rx-picker-apply";
  const cancelButton = button("Cancel", "rx-btn", () => options.cancel?.());
  cancelButton.dataset["testid"] = "rx-picker-cancel";
  foot.append(cancelButton, applyButton);
  const hint = node("p", "rx-picker-hint");
  hint.dataset["testid"] = "rx-picker-hint";
  let chipsOpen = false;
  body.append(find, strip, list, chips);
  if (inline) {
    root.classList.add("inline");
    root.append(modes, hint, body);
  } else root.append(head, modes, body, foot);

  const box = (kind: "none" | "some" | "all", round: boolean): HTMLElement => {
    const element = node("span", `rx-ck${kind === "all" ? " on" : kind === "some" ? " mid" : ""}${round ? " rd" : ""}`);
    element.setAttribute("aria-hidden", "true");
    return element;
  };
  const row = (site: PickerLocation, child: boolean): HTMLElement => {
    const chosen = value.ids.includes(site.id);
    const element = node("button", `rx-pr${child ? " child" : ""}`);
    element.type = "button";
    element.setAttribute("role", value.mode === "one" ? "radio" : "checkbox");
    element.setAttribute("aria-checked", String(chosen));
    element.dataset["testid"] = `rx-picker-row-${site.id}`;
    element.append(box(chosen ? "all" : "none", value.mode === "one"), node("span", "rx-pr-name", site.name));
    const detail = site.note ?? (child ? site.state : site.franchisee === site.name ? undefined : site.franchisee);
    if (detail !== undefined && detail !== "") element.append(node("span", "rx-pr-note", detail));
    if (site.suspended === true) element.append(node("span", "rx-pr-note rx-pr-suspended", SUSPENDED_LABEL));
    element.addEventListener("click", () => {
      value = toggleLocation(value, site.id);
      draw();
    });
    return element;
  };
  function drawModes(): void {
    modes.replaceChildren();
    for (const mode of options.modes) {
      const choice = button(labels[mode], value.mode === mode ? "on" : "", () => {
        value = withMode(value, mode);
        draw();
      });
      choice.setAttribute("aria-pressed", String(value.mode === mode));
      choice.dataset["testid"] = `rx-picker-mode-${mode}`;
      modes.append(choice);
    }
  }
  function drawBody(): void {
    hint.hidden = !inline || options.modeHints?.[value.mode] === undefined;
    hint.textContent = options.modeHints?.[value.mode] ?? "";
    body.hidden = value.mode === "all";
    if (value.mode === "all") return;
    const base = chosenOnly ? options.locations.filter((site) => value.ids.includes(site.id)) : options.locations;
    const visible = filterLocations(base, query, state, hideSuspended);
    strip.replaceChildren();
    if (value.mode === "selected") {
      const all = button(`Select all ${visible.length}`, "rx-link", () => {
        value = selectAll(value, visible);
        draw();
      });
      all.dataset["testid"] = "rx-picker-select-all";
      const clear = button("Clear", "rx-link", () => {
        value = clearAll();
        draw();
      });
      clear.dataset["testid"] = "rx-picker-clear";
      const only = button("Chosen only", `rx-link${chosenOnly ? " on" : ""}`, () => {
        chosenOnly = !chosenOnly;
        draw();
      });
      only.setAttribute("aria-pressed", String(chosenOnly));
      strip.append(all, clear, only);
    }
    if (hasSuspended(options.locations)) {
      const hide = button(HIDE_SUSPENDED_LABEL, `rx-link${hideSuspended ? " on" : ""}`, () => {
        hideSuspended = !hideSuspended;
        draw();
      });
      hide.setAttribute("aria-pressed", String(hideSuspended));
      hide.dataset["testid"] = "rx-picker-hide-suspended";
      strip.append(hide);
    }
    const count = node("span", "rx-picker-count", countText(value, options.locations, words.plural));
    count.dataset["testid"] = "rx-picker-count";
    count.setAttribute("aria-live", "polite");
    strip.append(count);
    list.replaceChildren();
    if (visible.length === 0) list.append(node("p", "rx-picker-none", `No ${words.plural} match.`));
    for (const group of pickerGroups(options.locations, visible)) {
      if (group.franchisee === null) {
        list.append(row(group.locations[0] as PickerLocation, false));
        continue;
      }
      const heading = group.franchisee;
      const total = options.locations.filter((site) => site.franchisee === heading).length;
      const header = node("div", "rx-pr group");
      const fold = button(collapsed.has(heading) ? "▸" : "▾", "rx-fold", () => {
        if (collapsed.has(heading)) collapsed.delete(heading);
        else collapsed.add(heading);
        draw();
      });
      fold.setAttribute("aria-label", `${collapsed.has(heading) ? "Show" : "Hide"} ${heading}`);
      fold.setAttribute("aria-expanded", String(!collapsed.has(heading)));
      const gstate = groupState(group, value.ids);
      const toggle = node("button", "rx-pr-group-toggle");
      toggle.type = "button";
      toggle.dataset["testid"] = `rx-picker-group-${heading}`;
      if (value.mode === "selected") {
        toggle.setAttribute("role", "checkbox");
        toggle.setAttribute("aria-checked", gstate === "some" ? "mixed" : String(gstate === "all"));
        toggle.append(box(gstate, false));
        toggle.addEventListener("click", () => {
          value = toggleGroup(value, group);
          draw();
        });
      }
      toggle.append(node("span", "rx-pr-name", heading));
      header.append(fold, toggle, node("span", "rx-pr-note", `${group.locations.filter((site) => value.ids.includes(site.id)).length} of ${total}`));
      list.append(header);
      if (!collapsed.has(heading)) for (const site of group.locations) list.append(row(site, true));
    }
    chips.replaceChildren();
    chips.hidden = value.mode !== "selected" || value.ids.length === 0;
    const chosen = value.ids.flatMap((id) => options.locations.filter((candidate) => candidate.id === id));
    const split = inline ? chipSplit(chosen, chipsOpen) : { shown: chosen, hidden: 0 };
    for (const site of split.shown) {
      const id = site.id;
      const chip = node("span", "rx-chip");
      const remove = button("×", "rx-chip-x", () => {
        value = toggleLocation(value, id);
        draw();
      });
      remove.setAttribute("aria-label", `Remove ${site.name}`);
      chip.append(node("span", undefined, site.name), remove);
      chips.append(chip);
    }
    if (inline && (split.hidden > 0 || (chipsOpen && chosen.length > CHIP_CAP))) {
      const more = button(split.hidden > 0 ? `+${split.hidden} more` : "Show fewer", "rx-chip more", () => {
        chipsOpen = !chipsOpen;
        draw();
      });
      more.dataset["testid"] = "rx-picker-chips-more";
      chips.append(more);
    }
  }
  function draw(notify = true): void {
    drawModes();
    drawBody();
    applyButton.disabled = !canApply(value);
    if (notify) options.onChange?.(value);
  }
  search.addEventListener("input", () => {
    query = search.value;
    drawBody();
  });
  stateSelect.addEventListener("change", () => {
    state = stateSelect.value;
    drawBody();
  });
  draw(false);
  return root;
}
